import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Eye, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const receiptUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data?.path || r.data?.storage_path || "";
};

const useUpload = (onPath) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current?.click();
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await onPath(await uploadFile(file)); toast.success("Comprovante anexado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao anexar"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const input = (testid) => (
    <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid={testid} />
  );
  return { busy, pick, input };
};

// Form field used when creating a launch.
export function ReceiptField({ value, onChange, testid = "receipt" }) {
  const { busy, pick, input } = useUpload(async (p) => onChange(p));
  return (
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">Nota / comprovante (opcional)</label>
      {input(`${testid}-input`)}
      {value ? (
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 h-10 text-sm">
          <Paperclip className="w-4 h-4 text-sky-700 shrink-0" />
          <a href={receiptUrl(value)} target="_blank" rel="noreferrer" className="flex-1 min-w-0 text-sky-700 font-semibold truncate hover:underline" data-testid={`${testid}-view`}>
            <Eye className="w-3.5 h-3.5 inline mr-1" />Comprovante anexado
          </a>
          <button type="button" onClick={() => onChange("")} className="text-slate-400 hover:text-rose-600" aria-label="Remover" data-testid={`${testid}-remove`}><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={pick} disabled={busy} className="w-full bg-white" data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Paperclip className="w-4 h-4 mr-1.5" />} Anexar foto ou PDF
        </Button>
      )}
    </div>
  );
}

// Inline control on list rows: view, or attach later.
export function ReceiptInline({ kind, id, path, onDone, testid }) {
  const { busy, pick, input } = useUpload(async (p) => {
    await api.patch(`/receipts/${kind}/${id}`, { receipt_path: p });
    onDone?.();
  });
  if (path) {
    return (
      <a href={receiptUrl(path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-sky-700 hover:underline" data-testid={`${testid}-view`}>
        <Paperclip className="w-3 h-3" /> Ver comprovante
      </a>
    );
  }
  return (
    <>
      {input(`${testid}-input`)}
      <button type="button" onClick={pick} disabled={busy} className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-400 hover:text-sky-700" data-testid={`${testid}-attach`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Anexar comprovante
      </button>
    </>
  );
}
